import {
    ActionType,
    businessVerificationSettingsPath,
    OfficeVerificationStatus,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { SupportEmailLink } from "components/app/support-email-link";
import { trpc } from "components/rpc/trpc";
import { InlineBanner } from "components/ui/inline-banner";
import { InternalLink } from "components/ui/internal-link";
import { PrimaryButton } from "components/ui/primary-button";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useFeature, useIsAllowed, useRegionId } from "store/selectors/hooks";

type UnverifiedStatus = Exclude<OfficeVerificationStatus, "verified">;

interface PersonalPhoneEmptyStateProps {
    onReserve: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const ReservePhoneNumberPrompt: React.FC<PersonalPhoneEmptyStateProps> = ({
    onReserve,
}) => (
    <>
        <InlineBanner type="info">
            No personal phone number has been created yet. Reserve a number to get started
        </InlineBanner>
        <PrimaryButton onClick={onReserve}>Reserve phone number</PrimaryButton>
    </>
);

const ContactSupportLink: React.FC = () => (
    <SupportEmailLink
        label="Contact Support"
        subject="Start SMS & Voice Business Verification"
    />
);

const useCanManageIntegrations = (): boolean =>
    useIsAllowed(ActionType.ManageIntegrations);

const NotStartedLine: React.FC = () =>
    useCanManageIntegrations() ? (
        <>
            <ContactSupportLink /> to get started.
        </>
    ) : (
        <>Ask your admin to contact Support.</>
    );

const BusinessVerificationTabLink: React.FC = () =>
    useCanManageIntegrations() ? (
        <>
            {" "}
            <InternalLink to={businessVerificationSettingsPath}>
                View business verification
            </InternalLink>
        </>
    ) : null;

const StatusLine: React.FC<{ status: UnverifiedStatus }> = ({ status }) => {
    switch (status) {
        case "notStarted":
            return <NotStartedLine />;
        case "draft":
            return <>Our team is preparing it.</>;
        case "inReview":
            return (
                <>
                    It&apos;s being reviewed. We&apos;ll email your agency when
                    there&apos;s a decision.
                </>
            );
        case "needsAttention":
            return (
                <>
                    It couldn&apos;t be verified yet. We&apos;ll contact your agency if we
                    need anything.
                </>
            );
    }
    const exhaustiveCheck: never = status;
    throw new Error(`Unhandled office verification status: ${exhaustiveCheck}`);
};

const VerifyBusinessFirst: React.FC<{ status: UnverifiedStatus }> = ({ status }) => (
    <InlineBanner type="info">
        <div className="space-y-2">
            <p className="font-semibold">Business verification needed</p>
            <p>You can reserve a phone number once your business is verified.</p>
            <p>
                <StatusLine status={status} />
                <BusinessVerificationTabLink />
            </p>
        </div>
    </InlineBanner>
);

const VerificationGatedPrompt: React.FC<PersonalPhoneEmptyStateProps> = ({
    onReserve,
}) => {
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const statusQuery = useQuery({
        ...trpcClient.businessProfile.officeVerificationStatus.queryOptions({ regionId }),
        enabled: !!regionId,
    });

    if (statusQuery.isError) {
        return (
            <RetryableErrorBanner
                message="We couldn't check your business verification."
                retrying={statusQuery.isFetching}
                onRetry={() => {
                    statusQuery.refetch().catch((): void => undefined);
                }}
            />
        );
    }
    if (!statusQuery.data) {
        return <Spinner />;
    }
    if (statusQuery.data === "verified") {
        return <ReservePhoneNumberPrompt onReserve={onReserve} />;
    }
    return <VerifyBusinessFirst status={statusQuery.data} />;
};

export const PersonalPhoneEmptyState: React.FC<PersonalPhoneEmptyStateProps> = ({
    onReserve,
}) => {
    const isBusinessVerificationEnabled = useFeature("businessVerification");
    return isBusinessVerificationEnabled ? (
        <VerificationGatedPrompt onReserve={onReserve} />
    ) : (
        <ReservePhoneNumberPrompt onReserve={onReserve} />
    );
};
